/* calendar-views.jsx — Month, Week and Agenda renderers. */
const CV_HOUR_START = 7;
const CV_HOUR_END = 20;
const CV_HOUR_PX = 48;

function evClass(status) { return CAL.STATUS_TONE[status] || "scheduled"; }
function evStyleVars() { return null; }
function isMilestoneItem(m) { return m.kind === "milestone"; }
function milestoneLabel(m) { return m.completed ? "Completed milestone" : m.overdue ? "Overdue milestone" : "Milestone due"; }
function milestoneStatusLabel(m) { return m.completed ? "Completed" : m.overdue ? "Overdue" : "Due"; }
function milestonePillKind(m) { return m.completed ? "Active" : m.overdue ? "Overdue" : "Partial"; }

/* Build a 6-row month matrix (Mon-first) for the month containing `anchorIso`. */
function monthMatrix(anchorIso) {
  const first = anchorIso.slice(0, 8) + "01";
  const gridStart = CAL.startOfWeek(first);
  return Array.from({ length: 42 }, (_, i) => CAL.addDays(gridStart, i));
}

/* Greedy column packing for overlapping events within one day. */
function packDay(events) {
  const evs = events
    .map((m) => ({ m, s: CAL.minutesOf(m.start), e: Math.max(CAL.minutesOf(m.end), CAL.minutesOf(m.start) + 20) }))
    .sort((a, b) => a.s - b.s || a.e - b.e);
  let cluster = [], clusterEnd = -1;
  const out = [];
  const flush = () => {
    if (!cluster.length) return;
    const cols = [];
    cluster.forEach((it) => {
      let placed = false;
      for (let i = 0; i < cols.length; i++) { if (it.s >= cols[i]) { it.col = i; cols[i] = it.e; placed = true; break; } }
      if (!placed) { it.col = cols.length; cols.push(it.e); }
    });
    cluster.forEach((it) => { it.cols = cols.length; out.push(it); });
    cluster = []; clusterEnd = -1;
  };
  evs.forEach((it) => {
    if (cluster.length && it.s >= clusterEnd) flush();
    cluster.push(it); clusterEnd = Math.max(clusterEnd, it.e);
  });
  flush();
  return out;
}

function meetingsByDay(meetings) {
  const map = {};
  meetings.forEach((m) => { (map[m.date] = map[m.date] || []).push(m); });
  Object.values(map).forEach((arr) => arr.sort((a, b) => CAL.minutesOf(a.start) - CAL.minutesOf(b.start)));
  return map;
}

/* ============================ MONTH ============================ */
function MonthView({ meetings, anchorIso, onOpen, onCreate, onPickDay }) {
  const cells = monthMatrix(anchorIso);
  const byDay = meetingsByDay(meetings);
  const today = CAL.todayIso();
  const curMonth = anchorIso.slice(0, 7);
  const MAX = 3;

  return (
    <div className="cal-month">
      <div className="cal-dow">{CAL.WEEKDAYS.map((d) => <div key={d}>{d}</div>)}</div>
      <div className="cal-grid">
        {cells.map((iso) => {
          const day = byDay[iso] || [];
          const shown = day.slice(0, MAX);
          const extra = day.length - shown.length;
          const weekend = CAL.dow(iso) >= 5;
          const cls = ["cal-cell", iso === today ? "today" : "", iso.slice(0, 7) !== curMonth ? "other" : "", weekend ? "weekend" : ""].filter(Boolean).join(" ");
          return (
            <div key={iso} className={cls} onClick={() => onCreate(iso)}>
              <div className="cal-cell-head">
                <span className="cal-daynum">{Number(iso.slice(8, 10))}</span>
                <button className="cal-cell-add" title="New meeting" onClick={(e) => { e.stopPropagation(); onCreate(iso); }}><Icon name="plus" size={13} /></button>
              </div>
              <div className="cal-events">
                {shown.map((m) => (
                  <button key={m.id} className={`ev ${evClass(m.status)} ${isMilestoneItem(m) ? "milestone" : ""}`} onClick={(e) => { e.stopPropagation(); if (!isMilestoneItem(m)) onOpen(m); }} title={isMilestoneItem(m) ? `${m.projectName || "Project"} · ${milestoneLabel(m)}` : m.title}>
                    <span className="ev-time">{isMilestoneItem(m) ? "Due" : CAL.fmtTime(m.start)}</span>
                    <span className="ev-title">{m.title}</span>
                  </button>
                ))}
                {extra > 0 && <span className="cal-more" onClick={(e) => { e.stopPropagation(); onPickDay(iso); }}>+{extra} more</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================ WEEK ============================ */
function WeekView({ meetings, anchorIso, onOpen, onCreate }) {
  const weekStart = CAL.startOfWeek(anchorIso);
  const days = Array.from({ length: 7 }, (_, i) => CAL.addDays(weekStart, i));
  const byDay = meetingsByDay(meetings);
  const today = CAL.todayIso();
  const hours = Array.from({ length: CV_HOUR_END - CV_HOUR_START + 1 }, (_, i) => CV_HOUR_START + i);
  const bodyHeight = (CV_HOUR_END - CV_HOUR_START) * CV_HOUR_PX;
  const topFor = (min) => ((min - CV_HOUR_START * 60) / 60) * CV_HOUR_PX;

  const scrollRef = React.useRef(null);
  React.useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = Math.max(0, topFor(8 * 60) - 12); }, [anchorIso]);

  const nowMin = (() => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); })();
  const showNow = days.includes(today) && nowMin >= CV_HOUR_START * 60 && nowMin <= CV_HOUR_END * 60;

  const handleSlot = (iso, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const min = Math.round((y / CV_HOUR_PX * 60 + CV_HOUR_START * 60) / 30) * 30;
    const h = Math.floor(min / 60), m = min % 60;
    const end = min + 60;
    onCreate(iso, `${CAL.pad(h)}:${CAL.pad(m)}`, `${CAL.pad(Math.floor(end / 60))}:${CAL.pad(end % 60)}`);
  };

  return (
    <div className="cal-week" style={{ maxHeight: "calc(100vh - 250px)" }}>
      <div className="cal-week-head">
        <div className="corner" />
        {days.map((iso) => {
          const weekend = CAL.dow(iso) >= 5;
          return (
            <div key={iso} className={`cal-wday ${iso === today ? "today" : ""} ${weekend ? "weekend" : ""}`}>
              <span className="wd">{CAL.WEEKDAYS[CAL.dow(iso)]}</span>
              <span className="wn">{Number(iso.slice(8, 10))}</span>
            </div>
          );
        })}
      </div>
      <div className="cal-week-scroll" ref={scrollRef}>
        <div className="cal-week-body" style={{ height: bodyHeight }}>
          <div className="cal-hours">
            {hours.map((h) => <div key={h} className="cal-hour-label" style={{ height: CV_HOUR_PX }}><span>{h}:00</span></div>)}
          </div>
          {days.map((iso) => {
            const packed = packDay(byDay[iso] || []);
            const weekend = CAL.dow(iso) >= 5;
            return (
              <div key={iso} className={`cal-daycol ${weekend ? "weekend" : ""} ${iso === today ? "today" : ""}`} style={{ backgroundSize: `100% ${CV_HOUR_PX}px` }}>
                <div className="cal-slot" onClick={(e) => handleSlot(iso, e)} />
                {showNow && iso === today && <div className="cal-now-line" style={{ top: topFor(nowMin) }} />}
                {packed.map((it) => {
                  const top = topFor(it.s);
                  const height = Math.max(((it.e - it.s) / 60) * CV_HOUR_PX - 2, 18);
                  const w = 100 / it.cols;
                  return (
                    <button key={it.m.id} className={`ev ${evClass(it.m.status)} ${isMilestoneItem(it.m) ? "milestone" : ""}`} title={it.m.title}
                      onClick={(e) => { e.stopPropagation(); if (!isMilestoneItem(it.m)) onOpen(it.m); }}
                      style={{ top, height, left: `calc(${it.col * w}% + 3px)`, width: `calc(${w}% - 6px)` }}>
                      <span className="ev-title">{it.m.title}</span>
                      {height > 30 && <span className="ev-sub">{isMilestoneItem(it.m) ? milestoneLabel(it.m) : `${CAL.fmtTime(it.m.start)}–${CAL.fmtTime(it.m.end)}`}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================ AGENDA ============================ */
function AttendeeStack({ meeting }) {
  const shown = meeting.attendees.slice(0, 4);
  const extra = meeting.attendees.length - shown.length;
  return (
    <div className="ar-avatars">
      {shown.map((a, i) => <Avatar key={i} name={CAL.attendeeLabel(a)} avatarKey={CAL.attendeeAvatarKey(a)} />)}
      {extra > 0 && <span className="more">+{extra}</span>}
    </div>
  );
}

function AgendaView({ meetings, anchorIso, onOpen }) {
  const curMonth = anchorIso.slice(0, 7);
  const monthMeetings = meetings.filter((m) => m.date.slice(0, 7) === curMonth);
  const byDay = meetingsByDay(monthMeetings);
  const days = Object.keys(byDay).sort();
  const today = CAL.todayIso();
  const STATUS_DOT = { unprocessed: "var(--st-unprocessed)", Scheduled: "var(--st-sched)", Done: "var(--st-done)", Cancelled: "var(--st-cancel)", milestoneDue: "var(--st-mile-due)", milestoneOverdue: "var(--st-mile-overdue)", milestoneDone: "var(--st-mile-done)" };

  if (!days.length) {
    return (
      <div className="cal-empty">
        <div className="ce-ic"><Icon name="calendar" size={22} /></div>
        <h3>No calendar items this month</h3>
        <div>Nothing scheduled for {CAL.MONTHS[Number(curMonth.slice(5)) - 1]}. Use “New meeting” to add one.</div>
      </div>
    );
  }

  return (
    <div className="cal-agenda">
      {days.map((iso) => {
        const rel = CAL.relativeDay(iso);
        const list = byDay[iso];
        return (
          <div key={iso} className="cal-agenda-day">
            <div className="cal-agenda-head">
              <span className="ad-date">{CAL.fmtDayLabel(iso)}</span>
              {rel && <span className="ad-rel">{rel}</span>}
              {iso === today && !rel && <span className="ad-rel">Today</span>}
              <span className="ad-count">{list.length} item{list.length !== 1 ? "s" : ""}</span>
            </div>
            {list.map((m) => {
              const proj = m.projectId ? CAL.projectById[m.projectId] : null;
              const milestone = isMilestoneItem(m);
              return (
                <div key={m.id} className={`cal-agenda-row ${m.status === "Cancelled" ? "cancelled" : ""} ${milestone ? `milestone ${evClass(m.status)}` : ""}`} onClick={() => { if (!milestone) onOpen(m); }}>
                  <div className="ar-time">
                    <span className="st-dot" style={{ background: STATUS_DOT[m.status] }} />
                    {milestone ? "Due" : CAL.fmtTime(m.start)}
                  </div>
                  <div className="ar-main">
                    <div className="ar-title"><span>{m.title}</span><span className={`type-tag ${milestone ? evClass(m.status) : ""}`}>{milestone ? milestoneLabel(m) : m.type}</span></div>
                    <div className="ar-meta">
                      {proj ? <span>{proj.name}</span> : <span className="faint">Internal · no project</span>}
                      <span className="dot-sep" />
                      {milestone
                        ? <span>{milestoneLabel(m)}</span>
                        : <span>{m.attendees.length} attendee{m.attendees.length !== 1 ? "s" : ""}</span>}
                      {!milestone && m.videoLink && <React.Fragment><span className="dot-sep" /><span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="video" size={12} />Video</span></React.Fragment>}
                    </div>
                  </div>
                  <div className="ar-right">
                    {!milestone && <AttendeeStack meeting={m} />}
                    <Pill kind={milestone ? milestonePillKind(m) : m.status === "Cancelled" ? "cancelled" : m.status === "Done" ? "completed" : m.status === "unprocessed" ? "unprocessed" : "active"} sm>{milestone ? milestoneStatusLabel(m) : m.status === "unprocessed" ? "Unprocessed" : m.status}</Pill>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

Object.assign(window, { MonthView, WeekView, AgendaView });
